import { useMemo, useState } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { useAppContext, useFolderContext } from '~/contexts';
import type { FolderTree } from '~/graphql/scalars';

export type Folder = {
  canUpdate: {
    value: boolean;
  };
  id: number;
  name: string;
};

export type FolderTreeResult = Folder & {
  isRoot: boolean;
  children: FolderTreeResult[];
};

export const useAccessibleFolders = (
  folderTree: FolderTree | undefined,
  folders: readonly Folder[] | undefined,
) => {
  const accessibleFolders = useMemo(() => {
    if (!folderTree || !folders) return [];

    const hasAccessibleChildren = (folder: FolderTreeResult): boolean => {
      if (folder.canUpdate.value) return true;
      return folder.children.some(hasAccessibleChildren);
    };

    const getAccessibleFolders = (folderNode: FolderTree): FolderTreeResult => {
      const folderData = folders.find((f) => f.id === folderNode.id);
      const canUpdate = folderData?.canUpdate.value ?? false;

      return {
        id: folderNode.id,
        name: folderNode.name,
        canUpdate: {
          value: canUpdate,
        },
        isRoot: false,
        children: folderNode.children
          .map(getAccessibleFolders)
          .filter((child) => child.canUpdate.value || child.children.length > 0),
      };
    };

    const topLevelFolders = folderTree.children
      .map(getAccessibleFolders)
      .filter((folder) => folder.canUpdate.value || hasAccessibleChildren(folder));

    // Add the root folder to the list of accessible folders if the user can update it
    const rootFolder = folders.find((folder) => folder.id === folderTree.id);
    if (rootFolder && rootFolder.canUpdate.value) {
      topLevelFolders.unshift({ ...rootFolder, isRoot: true, children: [] });
    }

    return topLevelFolders;
  }, [folderTree, folders]);

  const findFolder = (id: number, folders = accessibleFolders): FolderTreeResult | undefined => {
    for (const folder of folders) {
      if (folder.id === id) return folder;
      if (folder.children.length > 0) {
        const found = findFolder(id, folder.children);
        if (found) return found;
      }
    }
    return undefined;
  };

  /** Undefined for top-level folders and folders not in the tree */
  const findParent = (
    id: number | undefined,
    folders = accessibleFolders,
    parent?: FolderTreeResult,
  ): FolderTreeResult | undefined => {
    for (const folder of folders) {
      if (folder.id === id) return parent;
      const found = findParent(id, folder.children, folder);
      if (found) return found;
    }
    return undefined;
  };

  return { accessibleFolders, findFolder, findParent };
};

interface UseLastFolderOptions {
  enabled: boolean;
  defaultFolderId: number;
  storageContext: string;
}

export const useLastFolder = ({
  enabled,
  defaultFolderId,
  storageContext,
}: UseLastFolderOptions) => {
  const { currentNetwork } = useAppContext();
  const { folderTree, folders } = useFolderContext();
  const { findFolder } = useAccessibleFolders(folderTree, folders);

  const storageKey = `lastMediaFolder_${currentNetwork.id}_${storageContext}`;

  const [storedFolderId, setStoredFolderId] = useLocalStorage(storageKey, defaultFolderId);
  const [localFolderId, setLocalFolderId] = useState(defaultFolderId);

  const validatedFolderId =
    folders != null && findFolder(storedFolderId) == null ? defaultFolderId : storedFolderId;

  return enabled
    ? ([validatedFolderId, setStoredFolderId] as const)
    : ([localFolderId, setLocalFolderId] as const);
};
